// Oracle identifier helpers: UPPERCASE folding for unquoted, case-exact for
// `"Quoted"`. Pure module — no I/O, no Monaco runtime.
//
// Oracle identifier rules (per the Oracle SQL Language Reference):
//   * Bare identifiers match `^[A-Za-z][A-Za-z0-9_$#]*$` and are folded to
//     UPPERCASE before storage in the data dictionary. Quoted identifiers
//     (`"name"`) preserve case verbatim and may contain any characters.
//   * Doubled-quote escape (`""`) for a literal `"` inside the body.
//   * Identifier comparison is case-insensitive when both sides are
//     unquoted; case-sensitive when EITHER side is quoted (because quoting
//     opts into case-exact storage).
//
// This is the OPPOSITE convention from MSSQL/PG/MySQL/SQLite, where the
// fold-case for unquoted identifiers is lower-case. Keep the fold direction
// in sync with the engine's data-dictionary casing — Oracle's `ALL_OBJECTS`
// stores `EMPLOYEES`, not `employees`.

const BARE_IDENT_RE = /^[A-Za-z][A-Za-z0-9_$#]*$/;

export function needsDoubleQuote(identifier: string): boolean {
  if (identifier === '') return true;
  if (!BARE_IDENT_RE.test(identifier)) return true;
  // Oracle folds bare to UPPERCASE — if the input is mixed/lower case the
  // user is implicitly asking for case-exact, so we quote.
  return identifier !== identifier.toUpperCase();
}

export function doubleQuote(identifier: string): string {
  return `"${identifier.replace(/"/g, '""')}"`;
}

/** Round-trip-safe Oracle identifier — double-quoted only when required. */
export function formatIdentifier(identifier: string): string {
  return needsDoubleQuote(identifier) ? doubleQuote(identifier) : identifier;
}

/** Compose `schema.name` with per-part quote-on-demand. */
export function formatQualifiedTwoPart(schema: string, name: string): string {
  return `${formatIdentifier(schema)}.${formatIdentifier(name)}`;
}

/**
 * Strip the surrounding quotes from a single Oracle identifier token,
 * applying the doubled-`""` escape rule when the body uses it. Returns the
 * input verbatim when not quoted — caller is responsible for case-folding
 * via `foldIdentifier` for case-insensitive comparison.
 */
export function unquoteIdentifier(token: string): string {
  if (token.length < 2) return token;
  const first = token[0]!;
  const last = token[token.length - 1]!;
  if (first === '"' && last === '"') {
    return token.slice(1, -1).replace(/""/g, '"');
  }
  return token;
}

/**
 * Oracle identifier folding for case-comparison purposes.
 *
 * Quoted forms (`"…"`) preserve case verbatim after unquoting. Unquoted
 * identifiers fold to UPPERCASE — the Oracle data-dictionary's storage
 * convention. This matches the `ALL_OBJECTS.OBJECT_NAME` value the user
 * will see in the schema cache.
 */
export function foldIdentifier(token: string): string {
  if (token.length >= 2) {
    const first = token[0]!;
    const last = token[token.length - 1]!;
    if (first === '"' && last === '"') {
      return token.slice(1, -1).replace(/""/g, '"');
    }
  }
  return token.toUpperCase();
}

/**
 * Oracle identifier equality. Quoted vs unquoted comparisons fold the
 * unquoted side to UPPERCASE and compare case-sensitively; quoted-vs-quoted
 * compares the inner bodies case-sensitively. This mirrors how Oracle
 * resolves identifier references at parse time.
 */
export function eqIdentifier(a: string, b: string): boolean {
  return foldIdentifier(a) === foldIdentifier(b);
}
